import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaClipboardCheck, FaEdit, FaInbox, FaListUl, FaSync } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import DailyUpdate from "./DailyUpdate";
import ItemDrawer from "./ItemDrawer";
import { Empty, ItemCard, PageHead } from "./WorkUi";
import { errorMessage, fmtShortDate, fmtTime } from "./workApi";
import "../dm/dm.css";
import "./work.css";

const SECTIONS = [
  { key: "review", title: "Waiting for your review", tone: "" },
  { key: "overdue", title: "Late", tone: "wk-section--late" },
  { key: "today", title: "Today", tone: "" },
  { key: "upcoming", title: "Next 7 days", tone: "" },
  { key: "later", title: "Later / no date", tone: "" },
];

/** Everything assigned to me across projects, grouped by urgency, with one-tap daily update. */
function MyWork({ api }) {
  const notify = useNotify();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [openItem, setOpenItem] = useState(() => Number(params.get("item")) || null);
  const [openPack] = useState(() => params.get("pack") === "1");
  const [updateOpen, setUpdateOpen] = useState(() => params.get("update") === "1");
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.myWork());
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load your work"));
    }
  }, [api]);

  useEffect(() => {
    load();
    api.meta().then(setMeta).catch(() => null);
  }, [api, load]);

  useEffect(() => {
    if (params.get("item") || params.get("update") || params.get("pack")) setParams({}, { replace: true });
  }, [params, setParams]);

  const myOpen = useMemo(() => {
    if (!data) return [];
    const mine = ["overdue", "today", "upcoming", "later"].flatMap((k) => data.buckets[k]);
    return mine.filter((i) => !i.Is_Closed);
  }, [data]);

  const postedQuick = async (post) => {
    try {
      await api.markCalendarPostPosted(post.Post_Id, {});
      notify.success("Marked as posted");
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <div className="dmp wk">
      <PageHead title="My Work">
        {data ? <span className="wk-muted">Today, {fmtShortDate(data.today)}</span> : null}
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={load} aria-label="Refresh" title="Refresh">
          <FaSync aria-hidden="true" />
        </button>
        <Link className="dmp-btn dmp-btn--ghost" to={api.link("work/follow-ups")}>
          <FaListUl aria-hidden="true" /> Follow-ups
        </Link>
        <button type="button" className="dmp-btn dmp-btn--new" onClick={() => setUpdateOpen(true)} disabled={!data}>
          <FaEdit aria-hidden="true" /> Update my day
        </button>
      </PageHead>
      {error ? <div className="dmp-error">{error}</div> : null}

      {data ? (
        data.needs_update > 0 ? (
          <div className="wk-banner" role="status">
            <span>
              <strong>{data.needs_update}</strong> of your items have no update today. Your lead sees this on the team board.
            </span>
            <button type="button" className="dmp-btn" onClick={() => setUpdateOpen(true)}>
              Update now
            </button>
          </div>
        ) : data.updated_today ? (
          <div className="wk-banner wk-banner--ok" role="status">
            <span>
              <FaCheckCircle aria-hidden="true" /> You have updated everything for today.
            </span>
          </div>
        ) : null
      ) : null}

      {!data ? (
        <div className="dmp-card wk-muted">Loading…</div>
      ) : (
        <div className="wk-grid-2">
          <div>
            {SECTIONS.map((s) => {
              const rows = data.buckets[s.key] || [];
              if (!rows.length) return null;
              return (
                <section className={`wk-section ${s.tone}`} key={s.key} aria-label={s.title}>
                  <h2 className="wk-section__head">
                    {s.title} <span className="wk-section__count">{rows.length}</span>
                    {s.key === "review" ? (
                      <Link to={api.link("work/review")} className="dmp-link" style={{ fontSize: 13, fontWeight: 600, marginLeft: "auto" }}>
                        Open review inbox
                      </Link>
                    ) : null}
                  </h2>
                  {rows.map((item) => (
                    <ItemCard key={item.Item_Id} item={item} today={data.today} onOpen={(i) => setOpenItem(i.Item_Id)} />
                  ))}
                </section>
              );
            })}
            {!SECTIONS.some((s) => (data.buckets[s.key] || []).length) ? (
              <div className="dmp-card">
                <Empty icon={FaClipboardCheck} title="Nothing assigned to you right now">
                  New work shows up here and you will get an email when someone assigns it.
                </Empty>
              </div>
            ) : null}
            {data.buckets.done?.length ? (
              <section className="wk-section">
                <h2 className="wk-section__head">
                  <button type="button" className="dmp-link" onClick={() => setShowDone((v) => !v)} aria-expanded={showDone}>
                    Done in the last 7 days ({data.buckets.done.length})
                  </button>
                </h2>
                {showDone
                  ? data.buckets.done.map((item) => (
                      <ItemCard key={item.Item_Id} item={item} today={data.today} onOpen={(i) => setOpenItem(i.Item_Id)} />
                    ))
                  : null}
              </section>
            ) : null}
          </div>
          <aside>
            <div className="dmp-card">
              <h3 className="dmp-card__title">Posts assigned to you</h3>
              {data.posts.length ? (
                <ul className="wk-agenda">
                  {data.posts.map((p) => (
                    <li key={p.Post_Id} className="wk-agenda__row" style={{ cursor: "default" }}>
                      <span style={{ flex: 1 }}>
                        <strong>{p.Platform || "Social"}</strong> · {p.Title}
                        <div className={p.Is_Overdue ? "wk-due is-late" : "wk-muted"} style={{ fontSize: 12 }}>
                          {fmtShortDate(p.Date)}
                          {p.Time ? ` at ${fmtTime(p.Time)}` : ""} · {p.Status || "Draft"}
                        </div>
                      </span>
                      <button type="button" className="dmp-btn dmp-btn--sm" onClick={() => postedQuick(p)}>
                        Posted
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty icon={FaInbox} title="No posts due">
                  Planned social posts for the next week appear here.
                </Empty>
              )}
            </div>
          </aside>
        </div>
      )}

      {updateOpen && data ? (
        <DailyUpdate api={api} items={myOpen} generalNote={data.general_note} onClose={() => setUpdateOpen(false)} onSaved={load} />
      ) : null}
      {openItem ? (
        <ItemDrawer api={api} itemId={openItem} meta={meta} openPack={openPack} onClose={() => setOpenItem(null)} onChanged={load} />
      ) : null}
    </div>
  );
}

export default MyWork;
